import React from 'react';
import {
  Box, Button, ButtonBase, IconButton, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DropdownTextField from '../form-components/DropdownTextField';
import LocalText from '../common/LocalText';
import { C, btnBase } from '../../theme/adminTokens';
import { MIN_OPTIONS } from '../../constants/gameSchedule';
import { mapToValues, newOption } from '../../utils/gameSchedule';

const labelSx = { fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 };

// The question / option texts are maps { [localeId]: string }; DropdownTextField works on rows { localeId, value }.
const toRows = (map) => Object.entries(map ?? {}).map(([localeId, value]) => ({ localeId, value }));
const fromRows = (rows) => Object.fromEntries(rows.map((r) => [r.localeId, r.value]));
const isEmpty = (map) => mapToValues(map).length === 0;

// These two are shown under the input they belong to, so the note at the bottom leaves them out.
const FIELD_ISSUES = new Set(['Question text is missing', 'Every option needs text']);

/**
 * The right pane of the Build stage: one question's text and its options. Texts use the same per-language rows as the
 * rest of the CMS (DropdownTextField): the language sits inside each input, a row can be removed and a language added.
 * Everything is controlled: `question` in, the changed question out through `onChange`.
 */
export default function QuizQuestionEditor({
  question, index, total, onChange, readOnly = false, issues = [],
}) {
  const setText = (rows) => onChange({ ...question, text: fromRows(rows) });

  const setOption = (key, patch) =>
    onChange({ ...question, options: question.options.map((o) => (o._key === key ? { ...o, ...patch } : o)) });

  // Exactly one correct answer: picking one clears the others.
  const markCorrect = (key) =>
    onChange({ ...question, options: question.options.map((o) => ({ ...o, isCorrect: o._key === key })) });

  const addOption = () => onChange({ ...question, options: [...question.options, newOption()] });
  const removeOption = (key) => onChange({ ...question, options: question.options.filter((o) => o._key !== key) });

  const canRemoveOption = !readOnly && question.options.length > MIN_OPTIONS;
  const noteIssues = issues.filter((issue) => !FIELD_ISSUES.has(issue));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }} data-testid="question-editor">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.navy, letterSpacing: 0.6, textTransform: 'uppercase' }}>
          <LocalText>Question</LocalText> {index + 1} <LocalText>of</LocalText> {total}
        </Typography>
      </Box>

      <Box>
        <Typography sx={labelSx}><LocalText>Question text *</LocalText></Typography>
        <DropdownTextField
          label="Type the question"
          ariaLabel="Question text"
          values={toRows(question.text)}
          attribute="value"
          onChange={setText}
          multiline
          rows={2}
          compact
          disabled={readOnly}
          errorText={isEmpty(question.text) ? 'Question text is missing' : ''}
        />
      </Box>

      <Box>
        <Typography sx={labelSx}><LocalText>Options (select the correct answer)</LocalText></Typography>
        <Box role="radiogroup" aria-label="Correct answer" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {question.options.map((option, i) => (
            <Box
              key={option._key}
              data-testid="option-card"
              sx={{
                border: `1px solid ${option.isCorrect ? '#16a34a' : C.border}`, borderRadius: '12px', bgcolor: option.isCorrect ? '#f0fdf4' : '#ffffff',
                p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, transition: 'border-color .15s, background-color .15s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.navy }}><LocalText>Option</LocalText> {i + 1}</Typography>
                  <ButtonBase
                    role="radio"
                    aria-checked={option.isCorrect}
                    aria-label={`Option ${i + 1} is the correct answer`}
                    disabled={readOnly}
                    onClick={() => markCorrect(option._key)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '999px', fontSize: 11.5, fontWeight: 700,
                      border: `1px solid ${option.isCorrect ? '#16a34a' : C.border}`,
                      color: option.isCorrect ? '#ffffff' : C.muted, bgcolor: option.isCorrect ? '#16a34a' : '#ffffff',
                      '&:hover': { borderColor: '#16a34a' },
                    }}
                  >
                    {option.isCorrect ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} />}
                    {option.isCorrect ? 'Correct answer' : 'Mark as correct'}
                  </ButtonBase>
                </Box>
                {!readOnly && (
                  <Tooltip title={canRemoveOption ? 'Remove this option' : `A question needs at least ${MIN_OPTIONS} options`}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeOption(option._key)}
                        disabled={!canRemoveOption}
                        aria-label={`Remove option ${i + 1}`}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              <DropdownTextField
                label={`Option ${i + 1}`}
                ariaLabel={`Option ${i + 1} text`}
                values={toRows(option.text)}
                attribute="value"
                compact
                onChange={(rows) => setOption(option._key, { text: fromRows(rows) })}
                disabled={readOnly}
                errorText={isEmpty(option.text) ? 'Option text is missing' : ''}
              />
            </Box>
          ))}
        </Box>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={addOption}
            sx={{ ...btnBase, mt: 1, color: C.navy, fontSize: 12 }}
          >
            <LocalText>Add option</LocalText>
          </Button>
        )}
      </Box>

      {!readOnly && noteIssues.length > 0 && (
        <Box role="note" sx={{ px: 1.5, py: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
          {noteIssues.map((issue) => (
            <Typography key={issue} sx={{ fontSize: 12, color: '#92400e' }}>• <LocalText>{issue}</LocalText></Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
